import { useActiveCircle } from '@/hooks/use-active-circle';
import { useSaveAiRecommendations } from '@/hooks/use-ai-recommendations';
import { getConciergeGrants } from '@/hooks/use-concierge-permissions';
import { useCreateRecCollection } from '@/hooks/use-create-rec-collection';
import { useForceRefreshCircleRecommendations } from '@/hooks/use-recommendations/use-force-refresh-circle-recommendations';
import { createHasPermission } from '@calm/evexia-web-common/dist/circle';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import { logger } from '@rhg/libs/logger';
import { useMutation } from '@rhg/libs/react-query';
import { getCircleJwtRemainingMs, isCircleJwtExpired } from '@rhg/utils/circle';
import { useCallback, useRef, useState } from 'react';

/** Both writes reuse one circle, so its JWT must outlast the AI generation inside the first write. 2 minutes is the budget for that generation; raise it if generation can run longer. */
const MIN_HELD_JWT_REMAINING_MS = 2 * MINUTE_MS;

export interface useSaveAiRecommendationDataParams {
    conversationId: string;
}

/**
 * Which flow the attempt ran, decided from the refreshed circle it wrote with:
 * - `skipped`: nothing was written.
 * - `recommendations-only`: the recommendations were saved with no Care Plan, then refreshed.
 * - `plan`: the flow that creates a Care Plan, with its id when creation worked.
 */
export type SaveAiRecommendationOutcome =
    | { path: 'skipped' }
    | { path: 'recommendations-only' }
    | { path: 'plan'; collectionId?: string };

export interface UseSaveAiRecommendationDataOptions {
    /** Enrollment in the GA flag; GA also needs the read access the attempt checks on the refreshed circle. */
    isConciergeGAFlagEnrolled: boolean;
    /** The add-new-plan flow, which needs a Care Plan even outside GA. */
    isNewPlan?: boolean;
}

export function useSaveAiRecommendationData({
    isConciergeGAFlagEnrolled,
    isNewPlan = false,
}: UseSaveAiRecommendationDataOptions) {
    const { saveRecommendationDataAsync } = useSaveAiRecommendations();
    const { createRecCollectionAsync } = useCreateRecCollection();
    const { forceRefreshRecommendationsAsync } =
        useForceRefreshCircleRecommendations();
    const { activeCircle, refetch } = useActiveCircle();
    /* Both below values are used to determine whether to show a failure page on GA plan guide screen */
    const [hasPlanGenerationFailed, setHasPlanGenerationFailed] =
        useState(false);
    const [generatedCollectionId, setGeneratedCollectionId] = useState<
        string | undefined
    >();
    const latestAttemptRef = useRef(0);

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (
            params: useSaveAiRecommendationDataParams
        ): Promise<SaveAiRecommendationOutcome> => {
            const { conversationId } = params;

            /*
             * The failure flag and the generated plan are shared across every
             * attempt, so only the newest one may write them - an abandoned
             * attempt settling late
             * would otherwise report its result against whatever replaced it.
             * Numbered before the refetch, so dismissing the failure also
             * abandons an attempt still waiting on it.
             */
            const attempt = ++latestAttemptRef.current;
            const isLatestAttempt = () => latestAttemptRef.current === attempt;

            /*
             * The circle held in render has its JWT masked while a refresh is
             * pending, and the writes would go out unauthorized. Unless the
             * held JWT has comfortable time left, write with the circle a
             * refetch returns; cancelRefetch: false joins the provider's own
             * in-flight JWT refetch instead of cancelling it.
             */
            const hasComfortableJwt =
                (getCircleJwtRemainingMs(activeCircle?.authorizationJWT) ?? 0) >
                MIN_HELD_JWT_REMAINING_MS;
            const circle = hasComfortableJwt
                ? activeCircle
                : (await refetch({ cancelRefetch: false })).data;

            /* Dismissed or replaced while waiting, so the newer attempt owns the writes */
            if (!isLatestAttempt()) {
                return { path: 'skipped' };
            }

            /* A refetch can hand back a circle with no JWT, or the same expired one; isCircleJwtExpired treats a missing JWT as not expired */
            if (
                !circle?.authorizationJWT ||
                isCircleJwtExpired(circle.authorizationJWT)
            ) {
                logger.error(
                    'Could not refresh the circle authorization before saving AI recommendations',
                    { conversationId },
                    new Error('Circle authorization is unavailable')
                );
                setHasPlanGenerationFailed(true);
                setGeneratedCollectionId(undefined);
                return { path: 'skipped' };
            }

            /* Both calls below write concierge data, so this gates on create
             * rather than read - a read-only circle would only get a 403. */
            const { canCreateConcierge } = getConciergeGrants(
                createHasPermission(circle.cohortInfo?.permissionDetail?.perms)
            );

            if (!canCreateConcierge) {
                return { path: 'skipped' };
            }

            /* Create implies read, so a flag-enrolled writer is a GA user */
            const createsPlan = isConciergeGAFlagEnrolled || isNewPlan;

            /*
             * Refreshes with the circle the save wrote with; the held one is
             * masked while its JWT refetches. Only this flow refreshes:
             * createV1RecCollection is purely additive, while forceRefresh
             * recalculates and replaces recommendations, which would risk
             * discarding the Care Plan just created. The plan flows rely on
             * the invalidation in useCreateRecCollection instead.
             */
            const refreshRecommendationsOnly =
                async (): Promise<SaveAiRecommendationOutcome> => {
                    await forceRefreshRecommendationsAsync({ circle });
                    return { path: 'recommendations-only' };
                };

            try {
                await saveRecommendationDataAsync({
                    conversationId,
                    circle,
                });
            } catch (error) {
                logger.error(
                    'Error saving AI recommendations:',
                    { conversationId },
                    error
                );
                if (isLatestAttempt()) {
                    setHasPlanGenerationFailed(true);
                    setGeneratedCollectionId(undefined);
                }
                return createsPlan
                    ? { path: 'plan' }
                    : refreshRecommendationsOnly();
            }

            if (!createsPlan) {
                if (isLatestAttempt()) {
                    setHasPlanGenerationFailed(false);
                }
                return refreshRecommendationsOnly();
            }

            /*
             * Only group the conversation's recommendations into a Care Plan
             * once the AI Router has successfully generated them.
             */
            try {
                const { collection } = await createRecCollectionAsync({
                    conversationId,
                    circle,
                });

                if (isLatestAttempt()) {
                    setHasPlanGenerationFailed(false);
                    setGeneratedCollectionId(collection.id);
                }
                return { path: 'plan', collectionId: collection.id };
            } catch (error) {
                logger.error(
                    'Error creating rec collection for AI recommendations:',
                    { conversationId },
                    error
                );
                if (isLatestAttempt()) {
                    setHasPlanGenerationFailed(true);
                    setGeneratedCollectionId(undefined);
                }
            }

            return { path: 'plan' };
        },
    });

    /* Dismissing the failure abandons the retry behind it, so one still in flight can't reopen the screen the user just closed */
    const clearPlanGenerationFailure = useCallback(() => {
        latestAttemptRef.current += 1;
        setHasPlanGenerationFailed(false);
    }, []);

    const clearGeneratedCollectionId = useCallback(
        () => setGeneratedCollectionId(undefined),
        []
    );

    return {
        saveAiRecommendationData: mutate,
        saveAiRecommendationDataAsync: mutateAsync,
        hasPlanGenerationFailed,
        clearPlanGenerationFailure,
        generatedCollectionId,
        clearGeneratedCollectionId,
        ...otherProps,
    };
}
